/**
 * Wall-clock timezone for practices and running practices (Eastern US for now).
 * Backend stores practiceDate without a zone label; both sides treat it as this zone.
 */
export const SCHEDULE_TIME_ZONE = 'America/New_York';

const pickPart = (parts, type) => Number(parts.find((p) => p.type === type)?.value ?? 0);

function wallClockInZoneToDate({ year, month, day, hours, minutes, seconds }, timeZone) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const readWallClock = (ms) => {
    const parts = formatter.formatToParts(new Date(ms));
    return {
      year: pickPart(parts, 'year'),
      month: pickPart(parts, 'month'),
      day: pickPart(parts, 'day'),
      hours: pickPart(parts, 'hour') % 24,
      minutes: pickPart(parts, 'minute'),
      seconds: pickPart(parts, 'second'),
    };
  };

  const desired = Date.UTC(year, month - 1, day, hours, minutes, seconds);
  let guess = desired;
  for (let i = 0; i < 3; i += 1) {
    const got = readWallClock(guess);
    const actual = Date.UTC(got.year, got.month - 1, got.day, got.hours, got.minutes, got.seconds);
    guess += desired - actual;
  }
  return new Date(guess);
}

/** Parse backend LocalDateTime (no timezone) as wall-clock in {@link SCHEDULE_TIME_ZONE}. */
export function parseScheduleDateTime(dateStr) {
  if (!dateStr) return null;
  const raw = String(dateStr).trim();
  if (raw.includes('Z') || /[+-]\d{2}:\d{2}$/.test(raw)) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const [datePart, timePart = '00:00:00'] = raw.split('T');
  if (!datePart) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds = 0] = timePart.split(':').map(Number);
  if (!year || !month || !day) return null;
  return wallClockInZoneToDate(
    { year, month, day, hours: hours || 0, minutes: minutes || 0, seconds: seconds || 0 },
    SCHEDULE_TIME_ZONE
  );
}

export function toScheduleInputValue(dateStr) {
  const d = parseScheduleDateTime(dateStr);
  if (!d) return '';
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: SCHEDULE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const year = pickPart(parts, 'year');
  const month = String(pickPart(parts, 'month')).padStart(2, '0');
  const day = String(pickPart(parts, 'day')).padStart(2, '0');
  const hour = String(pickPart(parts, 'hour') % 24).padStart(2, '0');
  const minute = String(pickPart(parts, 'minute')).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export function formatScheduleDateTime(dateStr, options = {}) {
  const d = parseScheduleDateTime(dateStr);
  if (!d) return 'TBA';
  return d.toLocaleString('en-US', {
    timeZone: SCHEDULE_TIME_ZONE,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}
